import {
  ButtonDS,
  DialogModalDS,
  Spacer,
  TextCapitalized,
  Option,
} from "@qivia/ui";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  selectCompaniesSepaDirectDebitData,
  updateLimitBalanceDirectDebitAsync,
  updateStatementDirectDebitAsync,
} from "../sepaDirectDebitSlice";
import { StatementFrequency } from "../sepaDirectDebitApi";
import { StatementOptions } from "./statementOptions";
import { LimitBalanceOptions } from "./limitBalanceOptions";
import { selectAdminUser } from "../../homeSlice";

export const UpdateSepaDirectDebitOptionsTypeModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
  currentSepaDirectDebitCompanyUuid: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { visible, onCloseModal, currentSepaDirectDebitCompanyUuid } = props;
  const adminUser = useAppSelector(selectAdminUser);
  const sepaDirectDebitList = useAppSelector(
    selectCompaniesSepaDirectDebitData,
  );
  const [statementFrequency, setStatementFrequency] =
    useState<Option<StatementFrequency> | null>(null);

  const [displayError, setDisplayError] = useState<boolean>(false);

  const currentSepaDirectDebit = useMemo(() => {
    return sepaDirectDebitList.find(
      (t) => t.companyUuid === currentSepaDirectDebitCompanyUuid,
    );
  }, [sepaDirectDebitList, currentSepaDirectDebitCompanyUuid]);
  const [lowerLimit, setLowerLimit] = useState<number | "">(
    currentSepaDirectDebit?.lowerLimit ?? "",
  );
  const [upperLimit, setUpperLimit] = useState<number | "">(
    currentSepaDirectDebit?.upperLimit ?? "",
  );

  const resetModal = useCallback(() => {
    setStatementFrequency(null);
    setLowerLimit(currentSepaDirectDebit?.lowerLimit ?? "");
    setUpperLimit(currentSepaDirectDebit?.upperLimit ?? "");
    setDisplayError(false);
    onCloseModal();
  }, [onCloseModal, currentSepaDirectDebit]);

  const isStatementType = useMemo(() => {
    return currentSepaDirectDebit?.sepaDirectDebitType === "STATEMENT";
  }, [currentSepaDirectDebit]);

  const isLimitBalanceType = useMemo(() => {
    return currentSepaDirectDebit?.sepaDirectDebitType === "LIMIT_BALANCE";
  }, [currentSepaDirectDebit]);

  const isFormValidForStatement = useMemo(() => {
    return isStatementType && statementFrequency !== null;
  }, [statementFrequency, isStatementType]);

  const isFormValidForLimitBalance = useMemo(() => {
    return (
      isLimitBalanceType &&
      lowerLimit !== "" &&
      upperLimit !== "" &&
      lowerLimit < upperLimit
    );
  }, [lowerLimit, upperLimit, isLimitBalanceType]);

  const isFormValid = useMemo(() => {
    return isFormValidForStatement || isFormValidForLimitBalance;
  }, [isFormValidForStatement, isFormValidForLimitBalance]);

  const clickToSubmit = useCallback(() => {
    setDisplayError(true);
    if (!adminUser) {
      return;
    }

    if (isStatementType && statementFrequency) {
      void dispatch(
        updateStatementDirectDebitAsync({
          companyUuid: currentSepaDirectDebitCompanyUuid,
          frequency: statementFrequency.value,
          updatedBy: adminUser.email,
        }),
      );
    }
    if (isLimitBalanceType) {
      if (
        !isFormValidForLimitBalance ||
        lowerLimit === "" ||
        upperLimit === ""
      ) {
        return;
      }
      void dispatch(
        updateLimitBalanceDirectDebitAsync({
          companyUuid: currentSepaDirectDebitCompanyUuid,
          lowerLimit: lowerLimit,
          upperLimit: upperLimit,
          updatedBy: adminUser.email,
        }),
      );
    }
    resetModal();
  }, [
    adminUser,
    isStatementType,
    isLimitBalanceType,
    resetModal,
    statementFrequency,
    dispatch,
    currentSepaDirectDebitCompanyUuid,
    isFormValidForLimitBalance,
    lowerLimit,
    upperLimit,
  ]);

  if (!currentSepaDirectDebit) {
    return;
  }

  return (
    <DialogModalDS
      visible={visible}
      onClose={resetModal}
      title={t("updateSepaDirectDebitOptionsType.modal.title")}
      width={48}
      bottomButton={
        <ButtonDS
          text={t(`updateSepaDirectDebitOptionsType.modal.confirm`)}
          format={"fill"}
          buttonType={"primary"}
          disabled={!isFormValid}
          onClick={clickToSubmit}
        />
      }
    >
      <StyledColumn>
        <TextCapitalized>
          {t(`updateSepaDirectDebitOptionsType.modal.subtitle`)}
          {currentSepaDirectDebit.companyName}
        </TextCapitalized>
        <Spacer y={1.5} />
        {isStatementType && (
          <StatementOptions
            statementFrequency={statementFrequency}
            setStatementFrequency={setStatementFrequency}
            disabledFrequency={
              currentSepaDirectDebit.statementFrequency ?? undefined
            }
            displayError={displayError}
          />
        )}
        {isLimitBalanceType && (
          <LimitBalanceOptions
            lowerLimit={lowerLimit}
            setLowerLimit={setLowerLimit}
            upperLimit={upperLimit}
            setUpperLimit={setUpperLimit}
            displayError={displayError}
          />
        )}
      </StyledColumn>
    </DialogModalDS>
  );
};

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;
